import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import * as Icon from 'react-feather';
import {
  Button,
  Col,
  // Input,
  // Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row
} from 'reactstrap';

import FormInput from '../../../components/form/FormInput';
import FormToggle from '../../../components/form/FormToggle';

import {
  metaTemplatesOptionsGet,
  whatsappMetaTemplatesAdd,
  whatsappMetaTemplatesUpdate
} from '../../../store/slices/tenantSlices/configurationSlice';
import FormSelect from '../../../components/form/FormSelect';
import FormTextarea from '../../../components/form/FormTextarea';

const AddEditMetaTemplateModal = ({ isOpen, onCloseClick, isEdit = false, editRecord = null }) => {
  const dispatch = useDispatch();

  // const [fileName, setFileName] = useState('');
  const [metaTemplatesOptions, setMetaTemplatesOptions] = useState([]);
  // const fileInputRef = useRef(null);

  const initialValues = {
    name: '',
    is_header: 'false',
    is_footer: 'false',
    header_type: 'text',
    header_template_name: '',
    header_text: '',
    message_type: 'text',
    body: '',
    footer: '',
    buttons: [{ id: '', type: '', title: '' }],
    list_sections: [
      {
        title: '',
        rows: [
          {
            id: '',
            title: '',
            description: ''
          }
        ]
      }
    ],
    is_final: 'yes'
  };

  const validationSchema = Yup.object().shape({
    body: Yup.string().required('Body is required'),
    name: Yup.string().required('Name is required'),
    message_type: Yup.string()
      .oneOf(['text', 'list', 'button'])
      .required('Message type is required'),

    is_header: Yup.string().oneOf(['true', 'false']).required('Is header is required'),
    is_footer: Yup.string().oneOf(['true', 'false']).required('Is footer is required'),

    header_template_name: Yup.string().when('is_header', {
      is: 'true',
      then: (s) => s.required('Header template name is required'),
      otherwise: (s) => s.notRequired()
    }),

    header_text: Yup.string().when(['is_header', 'header_type'], {
      is: (is_header, header_type) => is_header === 'true' && header_type === 'text',
      then: (s) => s.required('Header text is required'),
      otherwise: (s) => s.notRequired()
    }),

    footer: Yup.string().when('is_footer', {
      is: 'true',
      then: (s) => s.required('Footer text is required'),
      otherwise: (s) => s.notRequired()
    }),

    buttons: Yup.array().when('message_type', {
      is: 'button',
      then: (s) =>
        s
          .of(
            Yup.object().shape({
              id: Yup.string().required('id is required'),
              type: Yup.string().required('type is required'),
              title: Yup.string().required('title is required')
            })
          )
          .min(1, 'At least one button is required'),
      otherwise: (s) => s.notRequired()
    }),

    list_sections: Yup.array().when('message_type', {
      is: 'list',
      then: (s) =>
        s
          .of(
            Yup.object().shape({
              title: Yup.string().required('title is required'),
              rows: Yup.array()
                .of(
                  Yup.object().shape({
                    id: Yup.string().required('id is required'),
                    title: Yup.string().required('title is required'),
                    description: Yup.string().required('description is required')
                  })
                )
                .min(1, 'At least one row is required')
            })
          )
          .min(1, 'At least one section is required'),
      otherwise: (s) => s.notRequired()
    })
  });

  const onSubmit = async () => {
    try {
      const payload = { ...values };
      if (values?.message_type === 'list') {
        delete payload['buttons'];
      } else if (values?.message_type === 'button') {
        delete payload['list_sections'];
      } else {
        delete payload['buttons'];
        delete payload['list_sections'];
      }

      if (values?.is_header === 'false') {
        payload['header_type'] = '';
      }

      delete payload['is_header'];
      delete payload['is_footer'];

      let response;
      if (isEdit) {
        response = await dispatch(
          whatsappMetaTemplatesUpdate({ ...payload, id: editRecord.id })
        ).unwrap();
      } else {
        response = await dispatch(whatsappMetaTemplatesAdd(payload)).unwrap();
      }

      if (response && response?.message) {
        toast.success(`Record ${isEdit ? 'Updated' : 'Added'} Successfully`);
        onCloseClick(true);
        // setFileName('');
        resetForm();
      } else {
        toast.error(response?.error || 'Something went wrong');
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  const getMetaTemplatesOptions = async () => {
    try {
      const response = await dispatch(metaTemplatesOptionsGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: val.name
        }));
        setMetaTemplatesOptions(options);
      } else {
        setMetaTemplatesOptions([]);
      }
    } catch (error) {
      setMetaTemplatesOptions([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    getMetaTemplatesOptions();
  }, []);

  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    touched,
    errors,
    resetForm,
    setValues,
    setFieldValue
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  });

  useEffect(() => {
    if (isEdit && editRecord) {
      setValues({
        name: editRecord.name || '',
        is_header: editRecord.header_type ? 'true' : 'false',
        is_footer: editRecord.footer ? 'true' : 'false',
        header_type: editRecord.header_type || 'text',
        header_template_name: editRecord.header_template_name || '',
        header_text: editRecord.header_text || '',
        message_type: editRecord.message_type || 'text',
        body: editRecord.body || '',
        footer: editRecord.footer || '',
        buttons: editRecord.buttons?.length
          ? editRecord.buttons
          : [{ id: '', type: '', title: '' }],
        list_sections: editRecord.list_sections?.length
          ? editRecord.list_sections
          : [
              {
                title: '',
                rows: [
                  {
                    id: '',
                    title: '',
                    description: ''
                  }
                ]
              }
            ],
        is_final: editRecord.is_final || 'yes'
      });
    }
  }, [editRecord, isEdit]);

  // const handleFileChange = (event) => {
  //   const file = event.target.files[0];
  //   if (file && (file.type === 'application/pdf' || file.type.startsWith('image/'))) {
  //     setFileName(file.name);
  //     setFieldValue('file', file);
  //   } else {
  //     toast.error('Please upload a PDF or image file.');
  //     fileInputRef.current.value = '';
  //     setFileName('');
  //   }
  // };

  const handleDynamicChange = (e, index, sectionIndex = null, isRow = false) => {
    const { name, value } = e.target;

    if (values.message_type === 'button') {
      const updatedButtons = structuredClone(values.buttons); // Deep clone
      updatedButtons[index][name] = value;
      setFieldValue('buttons', updatedButtons);
    }

    if (values.message_type === 'list') {
      const updatedSections = structuredClone(values.list_sections); // Deep clone
      if (isRow) {
        updatedSections[sectionIndex].rows[index][name] = value;
      } else {
        updatedSections[index][name] = value;
      }
      setFieldValue('list_sections', updatedSections);
    }
  };

  const handleAddClick = (sectionIndex = null, isRow = false) => {
    if (values.message_type === 'button') {
      setFieldValue('buttons', [...(values?.buttons || []), { id: '', type: '', title: '' }]);
    }

    if (values.message_type === 'list') {
      if (isRow && sectionIndex !== null) {
        const updatedSections = [...values.list_sections];
        updatedSections[sectionIndex].rows.push({
          id: '',
          title: '',
          description: ''
        });
        setFieldValue('list_sections', updatedSections);
      } else {
        setFieldValue('list_sections', [
          ...(values?.list_sections || []),
          { title: '', rows: [{ id: '', title: '', description: '' }] }
        ]);
      }
    }
  };

  const handleDelete = (index, sectionIndex = null, isRow = false) => {
    if (values.message_type === 'button') {
      const updatedButtons = [...values.buttons];
      updatedButtons.splice(index, 1);
      setFieldValue('buttons', updatedButtons);
    }

    if (values.message_type === 'list') {
      const updatedSections = [...values.list_sections];
      if (isRow && sectionIndex !== null) {
        updatedSections[sectionIndex].rows.splice(index, 1);
      } else {
        updatedSections.splice(index, 1);
      }
      setFieldValue('list_sections', updatedSections);
    }
  };

  // const handleDelete = (index) => {
  //   const updatedOptions = [...(values?.buttons || [])]?.filter((_, i) => i !== index);
  //   setFieldValue('buttons', updatedOptions);
  // };

  return (
    <Modal isOpen={isOpen} size="xl" centered>
      <ModalHeader className="justify-content-center py-2">
        <div className="fs-5">
          {isEdit ? 'Edit Whatsapp Meta Template' : 'Add Whatsapp Meta Template'}
        </div>
      </ModalHeader>
      <ModalBody className="px-4">
        <form id="whatsapp-meta-templates" onSubmit={handleSubmit}>
          <Row>
            <Col lg="6">
              <FormInput
                name="name"
                label="Name"
                placeholder="Enter Name"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormToggle
                trueName="Yes"
                falseName="No"
                name="is_final"
                label="Final"
                value={String(values.is_final === 'yes')}
                onChange={() => {
                  setFieldValue('is_final', values.is_final === 'yes' ? 'no' : 'yes');
                }}
              />
            </Col>
          </Row>
          <Row>
            <Col lg="3">
              <FormToggle
                name="is_header"
                label="Header"
                value={String(values.is_header === 'true')}
                onChange={(e) => {
                  setFieldValue('is_header', e);
                  if (values.is_header === 'true') {
                    setFieldValue('header_type', 'text');
                    // setFieldValue('file', '');
                    setFieldValue('header_text', '');
                    setFieldValue('header_template_name', '');
                    // setFileName('');
                  }
                }}
              />
            </Col>
          </Row>
          {values.is_header === 'true' ? (
            <Row>
              <Col lg="2">
                <FormToggle
                  name="header_type"
                  label="Type"
                  trueName="Text"
                  falseName="Media"
                  value={String(values.header_type === 'text')}
                  onChange={() => {
                    setFieldValue('header_type', values.header_type === 'text' ? 'media' : 'text');
                    if (values.header_type === 'text') {
                      // setFieldValue('file', '');
                      setFieldValue('header_text', '');
                      // setFileName('');
                    }
                  }}
                />
              </Col>
              <Col lg="3">
                <FormSelect
                  name="header_template_name"
                  label="Template name"
                  placeholder="Select template name"
                  value={values.header_template_name}
                  options={metaTemplatesOptions}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              {values.header_type === 'text' && (
                <Col lg="4">
                  <FormInput
                    name="header_text"
                    label="Header text"
                    placeholder="Enter header text"
                    value={values.header_text}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                  />
                </Col>
              )}
              {/* {values.header_type === 'media' ? (
                <Col lg="3">
                  <Label>Template Media</Label>
                  <div className="file-upload-container">
                    <Input
                      type="text"
                      className="file-name"
                      placeholder={fileName ? fileName : 'Choose file'}
                      readOnly
                    />
                    <label className="upload-button">
                      <Icon.Upload size={18} />
                      <Input
                        type="file"
                        id="file-upload"
                        className="file-upload"
                        onChange={handleFileChange}
                        accept="application/pdf,image/*"
                        ref={fileInputRef}
                      />
                    </label>
                  </div>
                </Col>
              ) : null} */}
            </Row>
          ) : null}
          <Row>
            <Col lg="3">
              <FormSelect
                name="message_type"
                label="Message Type"
                placeholder="Select message type"
                value={values.message_type}
                options={[
                  { label: 'Text', value: 'text' },
                  { label: 'Button', value: 'button' },
                  { label: 'List', value: 'list' }
                ]}
                onChange={(e) => {
                  setFieldValue('message_type', e.target.value);
                  setFieldValue('buttons', [{ id: '', type: '', title: '' }]);
                  setFieldValue('list_sections', [
                    {
                      title: '',
                      rows: [
                        {
                          id: '',
                          title: '',
                          description: ''
                        }
                      ]
                    }
                  ]);
                }}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="6">
              <FormTextarea
                name="body"
                label="Body"
                placeholder="Enter body"
                value={values.body}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
                rows={1}
              />
            </Col>
          </Row>
          {values?.message_type === 'button'
            ? values?.buttons?.map((x, index, array) => {
                return (
                  <Row key={index}>
                    <Col lg="3">
                      <FormInput
                        name="id"
                        label="Button Id"
                        placeholder="Enter button id"
                        value={x.id}
                        onChange={(e) => handleDynamicChange(e, index)}
                        onBlur={handleBlur}
                        touched={touched?.buttons?.[index]}
                        errors={errors?.buttons?.[index]}
                      />
                    </Col>
                    <Col lg="3">
                      <FormSelect
                        name="type"
                        label="Button Type"
                        placeholder="Select button type"
                        value={x.type}
                        options={[
                          { label: 'Reply', value: 'reply' },
                          { label: 'URL', value: 'url' }
                        ]}
                        onChange={(e) => handleDynamicChange(e, index)}
                        onBlur={handleBlur}
                        touched={touched?.buttons?.[index]}
                        errors={errors?.buttons?.[index]}
                      />
                    </Col>
                    <Col lg="3">
                      <FormInput
                        name="title"
                        label="Button Title"
                        placeholder="Enter button title"
                        value={x.title}
                        onChange={(e) => handleDynamicChange(e, index)}
                        onBlur={handleBlur}
                        touched={touched?.buttons?.[index]}
                        errors={errors?.buttons?.[index]}
                      />
                    </Col>
                    <Col lg="3" className="d-flex align-items-center gap-4 pt-2">
                      {(array?.length > 1 || index !== 0) && (
                        <Icon.Trash
                          size={20}
                          onClick={() => handleDelete(index)}
                          className="cursor-pointer text-danger"
                        />
                      )}
                      {array?.length - 1 === index && array.length < 4 && (
                        <Icon.Plus
                          className="cursor-pointer text-info"
                          size={20}
                          onClick={() => handleAddClick()}
                        />
                      )}
                    </Col>
                  </Row>
                );
              })
            : null}

          {values?.message_type === 'list'
            ? values?.list_sections?.map((x, index, array) => {
                return (
                  <Row key={index}>
                    <Col lg="4">
                      <FormInput
                        name="title"
                        label="List title"
                        placeholder="Enter list title"
                        value={x.title}
                        onChange={(e) => handleDynamicChange(e, index)}
                        onBlur={handleBlur}
                        touched={touched?.list_sections?.[index]}
                        errors={errors?.list_sections?.[index]}
                      />
                    </Col>
                    <Col
                      lg="7"
                      className="d-flex align-items-center justify-content-end gap-4 pt-2">
                      {(array?.length > 1 || index !== 0) && (
                        <Icon.Trash
                          size={20}
                          onClick={() => handleDelete(index)}
                          className="cursor-pointer text-danger"
                        />
                      )}
                      {array?.length - 1 === index && array.length < 10 && (
                        <Icon.Plus
                          className="cursor-pointer text-info"
                          size={20}
                          onClick={() => handleAddClick()}
                        />
                      )}
                    </Col>

                    {x.rows.map((val, idx, row_array) => {
                      return (
                        <Row key={idx} className="px-5">
                          <Col lg="2">
                            <FormInput
                              name="id"
                              label="Row Id"
                              placeholder="Enter row id"
                              value={val.id}
                              onChange={(e) => handleDynamicChange(e, idx, index, true)}
                              onBlur={handleBlur}
                              touched={touched?.list_sections?.[index]?.rows?.[idx]}
                              errors={errors?.list_sections?.[index]?.rows?.[idx]}
                            />
                          </Col>
                          <Col lg="4">
                            <FormInput
                              name="title"
                              label="Row Title"
                              placeholder="Enter row title"
                              value={val.title}
                              onChange={(e) => handleDynamicChange(e, idx, index, true)}
                              onBlur={handleBlur}
                              touched={touched?.list_sections?.[index]?.rows?.[idx]}
                              errors={errors?.list_sections?.[index]?.rows?.[idx]}
                            />
                          </Col>
                          <Col lg="4">
                            <FormInput
                              name="description"
                              label="Row description"
                              placeholder="Enter row description"
                              value={val.description}
                              onChange={(e) => handleDynamicChange(e, idx, index, true)}
                              onBlur={handleBlur}
                              touched={touched?.list_sections?.[index]?.rows?.[idx]}
                              errors={errors?.list_sections?.[index]?.rows?.[idx]}
                            />
                          </Col>

                          <Col lg="2" className="d-flex align-items-center gap-4 pt-2">
                            {(row_array?.length > 1 || idx !== 0) && (
                              <Icon.Trash
                                size={20}
                                onClick={() => handleDelete(idx, index, true)}
                                className="cursor-pointer text-danger"
                              />
                            )}
                            {row_array?.length - 1 === idx && row_array.length < 10 && (
                              <Icon.Plus
                                className="cursor-pointer text-info"
                                size={20}
                                onClick={() => handleAddClick(index, true)}
                              />
                            )}
                          </Col>
                        </Row>
                      );
                    })}
                  </Row>
                );
              })
            : null}
          <Row>
            <Col lg="2">
              <FormToggle
                name="is_footer"
                label="Footer"
                value={String(values.is_footer === 'true')}
                onChange={(e) => {
                  setFieldValue('is_footer', e);
                  if (values.is_footer === 'true') {
                    setFieldValue('footer', '');
                  }
                }}
              />
            </Col>
            {values.is_footer === 'true' ? (
              <Col lg="3">
                <FormInput
                  name="footer"
                  label="Footer text"
                  placeholder="Enter footer text"
                  value={values.footer}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
            ) : null}
          </Row>
        </form>
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={() => {
            onCloseClick();
            // setFileName('');
            resetForm();
          }}
          className="px-5 bg-transparent text-primary">
          Cancel
        </Button>
        <Button
          form="whatsapp-meta-templates"
          type="submit"
          className="px-5 bg-primary border-primary">
          {isEdit ? 'Update' : 'Save'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AddEditMetaTemplateModal.propTypes = {
  isOpen: PropTypes.bool,
  isEdit: PropTypes.bool,
  onCloseClick: PropTypes.func,
  editRecord: PropTypes.any
};

export default AddEditMetaTemplateModal;
